import { UserEvent, UserEventType } from '@shared/core/user-events'
import { useMutation, useQueryClient } from 'react-query'
import { ApiService } from 'src/services/api.service'
import { LIST_USER_EVENTS_CACHE_KEY } from './use-list-user-events'

type AddUserEventData = {
  eventName: UserEventType
  once?: boolean
}

type AddUserEventMutationContext = {
  snapshots: Array<{ queryKey: unknown[]; data: UserEvent[] | undefined }>
}

function createOptimisticUserEvent(eventName: UserEventType): UserEvent {
  return {
    id: -1,
    user_id: '',
    event: eventName,
    timestamp: new Date().toISOString(),
  }
}

export function useAddUserEvent() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, AddUserEventData, AddUserEventMutationContext>(
    ({ eventName, once }) => ApiService.UserEvents.addUserEvent(eventName, once),
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries([LIST_USER_EVENTS_CACHE_KEY])

        if (!variables.once) {
          await queryClient.invalidateQueries([LIST_USER_EVENTS_CACHE_KEY])
          return { snapshots: [] }
        }

        const snapshots: AddUserEventMutationContext['snapshots'] = []

        queryClient
          .getQueryCache()
          .findAll([LIST_USER_EVENTS_CACHE_KEY])
          .forEach((query) => {
            const queryKey = query.queryKey as unknown[]
            snapshots.push({
              queryKey,
              data: queryClient.getQueryData<UserEvent[]>(queryKey),
            })

            queryClient.setQueryData<UserEvent[]>(queryKey, (existing = []) => {
              if (existing.some((userEvent) => userEvent.event === variables.eventName)) {
                return existing
              }

              return [...existing, createOptimisticUserEvent(variables.eventName)]
            })
          })

        return { snapshots }
      },
      onError: (_error, _variables, context) => {
        context?.snapshots.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data)
        })
      },
      onSettled: () => {
        queryClient.invalidateQueries([LIST_USER_EVENTS_CACHE_KEY])
      },
    },
  )
}
